import React from "react";
import { AppleLogo, GooglePlayLogo, DownloadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";

/**
 * App Store + Google Play download buttons. All colors are inlined to be
 * bulletproof against parent CSS cascade (the surrounding page changes color
 * with light/dark theme — our buttons must NOT). The store badges always
 * appear as black pills with bright white text, exactly like the official
 * Apple/Google brand assets.
 *
 * URLs come from env vars so they can be filled in once the apps are
 * published without a code change:
 *   REACT_APP_APP_STORE_URL=https://apps.apple.com/dk/app/teamfit/id...
 *   REACT_APP_PLAY_STORE_URL=https://play.google.com/store/apps/details?id=dk.teamfit.app
 */
const APP_STORE_URL = process.env.REACT_APP_APP_STORE_URL || "";
const PLAY_STORE_URL = process.env.REACT_APP_PLAY_STORE_URL || "";

const SIZES = {
  sm: { height: 36, padX: 12, gap: 8, icon: 16, kicker: 9, label: 11 },
  md: { height: 48, padX: 16, gap: 12, icon: 22, kicker: 10, label: 14 },
  lg: { height: 56, padX: 20, gap: 12, icon: 26, kicker: 11, label: 16 },
};

export default function AppStoreButtons({ size = "md", className = "" }) {
  const { lang } = useI18n();
  const onMissing = (store) => {
    toast.info(
      lang === "da"
        ? `${store} kommer snart. Vi giver besked på support@teamfit.dk når den er klar.`
        : `${store} app is coming soon. We'll let you know via support@teamfit.dk.`,
    );
  };
  return (
    <div className={`flex items-center gap-3 flex-wrap ${className}`}>
      <StoreButton
        href={APP_STORE_URL}
        size={size}
        kicker={lang === "da" ? "Download fra" : "Download on the"}
        label="App Store"
        icon={<AppleLogo weight="fill" />}
        testId="store-button-apple"
        onMissing={() => onMissing("App Store")}
        lang={lang}
      />
      <StoreButton
        href={PLAY_STORE_URL}
        size={size}
        kicker={lang === "da" ? "Hent den på" : "Get it on"}
        label="Google Play"
        icon={<GooglePlayLogo weight="fill" />}
        testId="store-button-google"
        onMissing={() => onMissing("Google Play")}
        lang={lang}
      />
    </div>
  );
}

function StoreButton({ href, size, kicker, label, icon, testId, onMissing, lang }) {
  const isLive = Boolean(href);
  const s = SIZES[size] || SIZES.md;
  const wrap = (
    <span
      style={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        gap: `${s.gap}px`,
        height: `${s.height}px`,
        padding: `0 ${s.padX}px`,
        background: "#000000",
        color: "#FFFFFF",
        border: "1px solid rgba(255,255,255,0.15)",
        textDecoration: "none",
        lineHeight: 1,
      }}
    >
      <span style={{ display: "inline-flex", flexShrink: 0, color: "#FFFFFF", lineHeight: 0 }}>
        {React.cloneElement(icon, { size: s.icon, color: "#FFFFFF" })}
      </span>
      <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", lineHeight: 1 }}>
        <span
          style={{
            fontSize: `${s.kicker}px`,
            textTransform: "uppercase",
            letterSpacing: "0.18em",
            color: "rgba(255,255,255,0.75)",
            whiteSpace: "nowrap",
            fontWeight: 500,
          }}
        >
          {kicker}
        </span>
        <span
          style={{
            fontFamily: "'Barlow Condensed', sans-serif",
            fontWeight: 900,
            letterSpacing: "-0.01em",
            fontSize: `${s.label}px`,
            color: "#FFFFFF",
            whiteSpace: "nowrap",
            marginTop: "2px",
          }}
        >
          {label}
        </span>
      </span>
      {!isLive && (
        <span
          aria-hidden="true"
          style={{
            position: "absolute",
            top: "-8px",
            right: "-8px",
            padding: "2px 6px",
            fontSize: "9px",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.1em",
            background: "#FFCC00",
            color: "#000000",
          }}
        >
          {lang === "da" ? "SNART" : "SOON"}
        </span>
      )}
    </span>
  );
  const common = {
    "data-testid": testId,
    "aria-label": label,
    className: "active:scale-95 transition-transform",
    style: { textDecoration: "none" },
  };
  if (isLive) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" {...common}>
        {wrap}
      </a>
    );
  }
  return (
    <button type="button" onClick={onMissing} {...common}>
      {wrap}
    </button>
  );
}

export function CompactDownload({ testId = "compact-download" }) {
  const { lang } = useI18n();
  return (
    <div className="flex items-center gap-2" data-testid={testId}>
      <DownloadSimple weight="bold" size={14} className="text-[#34C759]" />
      <span className="text-xs uppercase tracking-widest font-bold">
        {lang === "da" ? "Hent appen" : "Get the app"}
      </span>
    </div>
  );
}
